import React from 'react';
import { motion } from 'framer-motion';
import { TestimonialCard } from './TestimonialCard';
import { testimonials } from '@/data/testimonials';
import { getCTAClasses, CTA_MESSAGES } from '@/lib/cta-config';

interface TestimonialsSectionProps {
  onGetStarted?: () => void;
}

export const TestimonialsSection: React.FC<TestimonialsSectionProps> = ({
  onGetStarted,
}) => {

  // Triple testimonials for ultra-smooth seamless scroll
  const tripleTestimonials = [...testimonials, ...testimonials, ...testimonials];

  return (
    <section className="py-16 bg-gradient-to-br from-teal-50/30 via-emerald-50/20 to-cyan-50/30 relative overflow-hidden">
      {/* Enhanced Background Effects */}
      <div className="absolute inset-0 overflow-hidden">
        {/* Large floating orbs */}
        <motion.div 
          className="absolute -top-40 -left-40 w-80 h-80 bg-gradient-to-br from-teal-400/8 to-emerald-400/8 rounded-full blur-3xl"
          animate={{
            x: [0, 60, 0],
            y: [0, -40, 0],
            scale: [1, 1.2, 1],
          }}
          transition={{
            duration: 25,
            repeat: Infinity,
            ease: "easeInOut"
          }}
        />
        
        <motion.div
          className="absolute top-32 -right-40 w-96 h-96 bg-gradient-to-br from-cyan-400/6 to-blue-400/6 rounded-full blur-3xl"
          animate={{
            x: [0, -50, 0],
            y: [0, 50, 0],
            scale: [1, 0.8, 1],
          }}
          transition={{
            duration: 30,
            repeat: Infinity,
            ease: "easeInOut"
          }}
        />

        <motion.div
          className="absolute bottom-32 left-1/3 w-72 h-72 bg-gradient-to-br from-emerald-400/7 to-teal-400/7 rounded-full blur-3xl"
          animate={{
            x: [0, 80, 0],
            y: [0, -60, 0],
            scale: [1, 1.3, 1],
          }}
          transition={{
            duration: 35,
            repeat: Infinity,
            ease: "easeInOut"
          }}
        />

        {/* Floating geometric shapes */}
        {Array.from({ length: 12 }).map((_, i) => (
          <motion.div
            key={i}
            className={`absolute w-3 h-3 rounded-full ${
              i % 4 === 0 ? 'bg-teal-300/20' : 
              i % 4 === 1 ? 'bg-emerald-300/20' : 
              i % 4 === 2 ? 'bg-cyan-300/20' : 'bg-blue-300/20'
            }`}
            style={{
              left: `${10 + (i * 8)}%`,
              top: `${15 + (i % 4) * 20}%`,
            }}
            animate={{
              y: [0, -120, 0],
              opacity: [0, 1, 0],
              scale: [1, 1.8, 1],
              rotate: [0, 180, 360],
            }}
            transition={{
              duration: 8 + i * 0.5,
              repeat: Infinity,
              ease: "easeInOut",
              delay: i * 0.6,
            }}
          />
        ))}

        {/* Animated grid pattern */}
        <motion.div 
          className="absolute inset-0 opacity-[0.03]"
          animate={{
            backgroundPosition: ['0% 0%', '100% 100%'],
          }}
          transition={{
            duration: 20,
            repeat: Infinity,
            ease: "linear"
          }}
          style={{
            backgroundImage: `
              linear-gradient(90deg, rgba(20, 184, 166, 0.3) 1px, transparent 1px),
              linear-gradient(rgba(20, 184, 166, 0.3) 1px, transparent 1px)
            `,
            backgroundSize: '60px 60px'
          }}
        />

        {/* Dynamic gradient overlay */}
        <motion.div 
          className="absolute inset-0"
          animate={{
            background: [
              'radial-gradient(circle at 20% 50%, rgba(20, 184, 166, 0.05) 0%, transparent 50%)',
              'radial-gradient(circle at 80% 50%, rgba(16, 185, 129, 0.05) 0%, transparent 50%)',
              'radial-gradient(circle at 50% 80%, rgba(6, 182, 212, 0.05) 0%, transparent 50%)',
              'radial-gradient(circle at 20% 50%, rgba(20, 184, 166, 0.05) 0%, transparent 50%)',
            ]
          }}
          transition={{
            duration: 15,
            repeat: Infinity,
            ease: "easeInOut"
          }}
        />
      </div>

      <div className="container mx-auto px-4 relative z-10">
        {/* Section header */}
        <motion.div
          className="text-center mb-12"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
        >
          <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-6">
            What Business Owners{' '}
            <span className="bg-gradient-to-r from-teal-600 via-emerald-600 to-cyan-600 bg-clip-text text-transparent">
              Are Saying
            </span>
          </h2>
          <p className="text-xl text-slate-600 max-w-3xl mx-auto leading-relaxed">
            Real stories from convenience store and gas station owners who&apos;ve transformed 
            their lottery payment management with LotteryFlow.
          </p>
        </motion.div>

        {/* Seamless Continuous Moving Carousel */}
        <div className="relative mb-16">
          <div className="overflow-hidden">
            <motion.div
              className="flex gap-6"
              animate={{
                x: [0, -(384 + 24) * testimonials.length]
              }}
              transition={{
                duration: 30, // Slower for better readability
                repeat: Infinity,
                ease: "linear",
                repeatType: "loop" // This ensures seamless looping
              }}
              style={{ 
                width: 'max-content',
                willChange: 'transform' // Optimize for animation performance
              }}
            >
              {tripleTestimonials.map((testimonial, index) => (
                <TestimonialCard
                  key={`${testimonial.id}-${Math.floor(index / testimonials.length)}-${index}`}
                  testimonial={testimonial}
                />
              ))}
            </motion.div>
          </div>
          
          {/* Enhanced fade edges matching background */}
          <div className="absolute inset-y-0 left-0 w-24 pointer-events-none z-10"
               style={{
                 background: 'linear-gradient(to right, rgba(240, 253, 250, 0.95), rgba(240, 253, 250, 0.7), transparent)'
               }} />
          <div className="absolute inset-y-0 right-0 w-24 pointer-events-none z-10"
               style={{
                 background: 'linear-gradient(to left, rgba(240, 253, 250, 0.95), rgba(240, 253, 250, 0.7), transparent)'
               }} />
        </div>

        {/* Enhanced Call to Action */}
        <motion.div
          className="text-center"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.4 }}
          viewport={{ once: true }}
        >
          <div className="bg-white/90 backdrop-blur-sm rounded-2xl p-8 md:p-10 shadow-xl border-2 border-teal-200/50 max-w-4xl mx-auto relative overflow-hidden">
            {/* CTA background gradient */}
            <div className="absolute inset-0 bg-gradient-to-br from-teal-50/30 via-emerald-50/20 to-cyan-50/30 rounded-2xl" />
            
            <div className="relative z-10">
              <h3 className="text-2xl md:text-3xl font-bold text-slate-900 mb-4">
                Ready to join these{' '}
                <span className="bg-gradient-to-r from-teal-600 to-emerald-600 bg-clip-text text-transparent">
                  successful
                </span>{' '}
                business owners?
              </h3>
              <p className="text-lg text-slate-600 mb-6 max-w-2xl mx-auto">
                Start your journey to effortless lottery payment management today.
              </p>
              {onGetStarted && (
                <motion.button
                  onClick={onGetStarted}
                  className={getCTAClasses('primary')}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  {CTA_MESSAGES.primary}
                </motion.button>
              )}
              <p className="text-sm text-slate-500 mt-3">
                No credit card required • Setup in under 5 minutes
              </p>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default TestimonialsSection;